/* eslint-disable no-underscore-dangle */
import produce from 'immer';
import {
  Component,
  Customization,
  CustomizationSpec,
} from '@/webapi/use-widget-catalog-api';
import { StoreStyles } from '@/features/editor/widgets/custom-widget/style-templating/models';

export function applyStoreStyle(
  customizations: Array<Customization>,
  storeStyles: StoreStyles,
  appId?: string,
  blueprintName?: string,
) {
  const normalized = normalizeStoreStyles(storeStyles);
  let hasChanged = false;

  const ret = produce(customizations, (draft) => {
    draft.forEach((cust) => {
      cust.components.forEach((comp) => {
        comp.specs.forEach((spec) => {
          const target = resolveStyleTarget(cust, comp, spec);
          if (!target) return;

          const changed = applyStyleToSpec(target, spec, normalized, {
            appId,
            blueprintName,
            custKey: cust.key,
            compKey: comp.key,
            compName: comp.name,
          });
          if (changed) {
            hasChanged = true;
          }
        });
      });
    });
  });

  return hasChanged ? ret : null;
}

function normalizeStoreStyles(storeStyles: StoreStyles) {
  const getTitleAttributes = () => {
    if (!storeStyles?.pdpTitleAttrs && !storeStyles.inner?.typography) {
      return undefined;
    }
    const transform = storeStyles.pdpTitleAttrs?.textTransform;
    const color = storeStyles.pdpTitleAttrs?.color;
    const fontSize = storeStyles.pdpTitleAttrs?.fontSize;
    const fontWeight = storeStyles.pdpTitleAttrs?.fontWeight;
    const fontFamily = storeStyles.pdpTitleAttrs?.fontFamily;
    const lineHeight = storeStyles.pdpTitleAttrs?.lineHeight;

    return {
      ...(color ? { color } : {}),
      ...(fontSize ? { fontSize, size: fontSize } : {}),
      ...(fontWeight ? { fontWeight } : {}),
      ...(fontFamily ? { fontFamily, font: fontFamily } : {}),
      ...(lineHeight ? { lineHeight } : {}),
      ...(transform ? { textTransform: transform, transform } : {}),
      ...storeStyles.inner?.typography?.title,
    };
  };
  const getPrimaryButton = () => {
    if (!storeStyles?.atcAttributes && !storeStyles?.inner?.buttons?.primary) {
      return undefined;
    }
    const textColor = storeStyles.atcAttributes?.textColor;
    const bgColor = storeStyles.atcAttributes?.bgColor;
    const border = storeStyles.atcAttributes?.border;
    const borderRadius = storeStyles.atcAttributes?.borderRadius;
    const boxShadow = storeStyles.atcAttributes?.boxShadow;

    return {
      ...(textColor ? { textColor } : {}),
      ...(bgColor ? { bgColor, background: bgColor } : {}),
      ...(border ? { border } : {}),
      ...(borderRadius ? { borderRadius } : {}),
      ...(boxShadow ? { boxShadow } : {}),
      ...storeStyles.inner?.buttons?.primary,
    };
  };
  const getImageAttributes = () => {
    if (!storeStyles.imageAttributes && !storeStyles.inner?.images?.default) {
      return undefined;
    }
    const border = storeStyles.imageAttributes?.border;
    const borderRadius = storeStyles.imageAttributes?.borderRadius;
    const boxShadow = storeStyles.imageAttributes?.boxShadow;
    return {
      ...(border ? { border } : {}),
      ...(borderRadius ? { borderRadius } : {}),
      ...(boxShadow ? { boxShadow } : {}),
      ...storeStyles.inner?.images?.default,
    };
  };
  return {
    buttons: {
      primary: getPrimaryButton(),
      secondary: storeStyles.inner?.buttons?.secondary,
    },
    text: {
      title: getTitleAttributes(),
      subtitle: storeStyles.inner?.typography?.subtitle,
    },
    images: {
      default: getImageAttributes(),
    },
    containers: {
      default: storeStyles.inner?.containers?.default,
    },
  };
}

function resolveStyleTarget(
  cust: Customization,
  comp: Component,
  spec: CustomizationSpec,
): string | null {
  if (spec.__def_style) return spec.__def_style;

  if (comp.key === `imgStyle`) return `images.default`;

  if (
    comp.name === `CTA Style` ||
    (cust.key === `atcButton` && comp.key === `style`) ||
    (cust.key === `cta` && comp.key === `style`) ||
    comp.key === `cta`
  ) {
    return `buttons.primary`;
  }

  if (comp.key === `title`) return `text.title`;
  if (comp.key === `subHeaderStyle`) return `text.subtitle`;

  if (comp.key === `container`) return `containers.default`;

  return null;
}

const extractStyleProperties = (spec: CustomizationSpec, style: any) => {
  let mapping: Record<string, string> = {};

  if (spec.__def_style) {
    mapping = style as Record<string, string>;
  } else if (isFont(spec)) {
    if (style.textColor) mapping.color = style.textColor;
    if (style.color) mapping.color = style.color;
    if (style.fontSize) mapping.fontSize = style.fontSize;
    if (style.fontWeight) mapping.fontWeight = style.fontWeight;
    if (style.fontFamily) mapping.fontFamily = style.fontFamily;
    if (style.lineHeight) mapping.lineHeight = style.lineHeight;
    if (style.textTransform) mapping.textTransform = style.textTransform;
    if (style.textAlign) mapping.textAlign = style.textAlign;
  } else if (isBackGround(spec)) {
    if (style.bgColor) mapping.value = style.bgColor;
    if (style.background) mapping.value = style.background;
  } else if (isBorder(spec)) {
    if (style.border) mapping.border = style.border;
    if (style.borderRadius) mapping.borderRadius = style.borderRadius;
  } else if (isShadow(spec)) {
    if (style.boxShadow) mapping.value = style.boxShadow;
  }
  return mapping;
};

const isEmptyStyle = (mapping: Record<string, string>) =>
  Object.keys(mapping).length === 0;

function applyStyleToSpec(
  target: string,
  spec: CustomizationSpec,
  normalized: any,
  context: {
    appId?: string;
    blueprintName?: string;
    custKey: string;
    compKey: string;
    compName: string;
  },
): boolean {
  const style = target
    ?.split(`.`)
    ?.reduce((acc, part) => acc?.[part], normalized);

  if (!style) {
    console.warn(`[StoreStyle] Unknown target: ${target}`, {
      ...context,
      specKey: spec?.key,
      specType: spec?.type,
    });
    return false;
  }

  let changed = false;
  const values = spec?.values;
  const mapping = extractStyleProperties(spec, style);

  if (isEmptyStyle(mapping)) {
    return false;
  }

  changed = applyStylesOnSubTree(values, mapping);

  if (changed && isBorder(spec) && `enabled` in values) {
    values.enabled = isBorderEnabled(style);
  }
  return changed;
}

const toKebabKey = (key: string) =>
  key.replace(/([a-z0-9])([A-Z])/g, `$1-$2`).toLowerCase();

const extractRichTextHtmlStyles = (styleString: string) =>
  styleString.split(`;`).reduce((acc, style) => {
    const [key, val] = style.split(`:`).map((s) => s.trim());
    if (key && val) {
      acc[key] = val;
    }
    return acc;
  }, {} as Record<string, string>);

export function decodeAndApplyStylesOnRichText(
  html: string,
  mapping: Record<string, any>,
): string {
  try {
    const decoded = decodeURIComponent(html);
    if (!decoded.startsWith(`<p`) || !decoded.endsWith(`</p>`)) {
      return html;
    }

    const updated = decoded.replace(
      /style="([^"]*)"/g,
      (_, styleString: string) => {
        const styles = extractRichTextHtmlStyles(styleString);

        Object.entries(mapping).forEach(([key, value]) => {
          const kebabKey = toKebabKey(key);
          if (kebabKey in styles && value) {
            styles[kebabKey] = value;
          }
        });

        return `style="${Object.entries(styles)
          .map(([k, v]) => `${k}: ${v}`)
          .join(`; `)}; "`;
      },
    );

    return encodeURIComponent(updated);
  } catch (e) {
    return html;
  }
}

export function applyStylesOnSubTree(
  obj: any,
  mapping: Record<string, any>,
): boolean {
  let changed = false;
  if (!obj || typeof obj !== `object`) return false;

  Object.entries(mapping).forEach(([key, value]) => {
    if (key in obj && obj[key] !== value && typeof value !== `undefined`) {
      obj[key] = value;
      changed = true;
    }
  });

  Object.keys(obj).forEach((k) => {
    if (isRichText(obj) && k === `value`) {
      const newVal = decodeAndApplyStylesOnRichText(obj.value, mapping);
      if (newVal !== obj.value) {
        obj[k] = newVal;
        changed = true;
      }
    }
    if (typeof obj[k] === `object`) {
      if (applyStylesOnSubTree(obj[k], mapping)) {
        changed = true;
      }
    }
  });

  return changed;
}

const isBorder = (spec: CustomizationSpec) =>
  spec.type === `border` || spec.key === `border`;

const isFont = (spec: CustomizationSpec) =>
  spec.type === `font` || spec.key === `font`;

const isBackGround = (spec: CustomizationSpec) =>
  spec.type === `background` || spec.key === `background`;

const isShadow = (spec: CustomizationSpec) =>
  spec.type === `shadow` || spec.key === `shadow`;

const isBorderEnabled = (style: any) => {
  const hasBorder = style.border && !style.border.includes(`0px`);
  const hasBorderRadius =
    style.borderRadius &&
    style.borderRadius !== `0` &&
    style.borderRadius !== `0px`;
  return hasBorder || hasBorderRadius;
};

const isRichText = (obj: any) =>
  !!obj?.value && typeof obj?.value === `string` && obj.value.includes(`%3Cp`);
